import React, { useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { MessageCard } from './MessageCard';
import { EmptyState } from './EmptyState';
import { formatMessageDateGroup } from '../utils';
import type { Message } from '../types';
import { MESSAGES_PAGE_CONFIG } from '../constants';

interface MessageListProps {
    messages: Message[];
    selectedId?: string;
    onSelect: (message: Message) => void;
    onMarkAsRead: (id: string) => void;
}

interface GroupedItem {
    type: 'header';
    date: string;
    id: string;
}

interface MessageItem {
    type: 'message';
    message: Message;
    index: number;
}

type ListItem = GroupedItem | MessageItem;

export const MessageList: React.FC<MessageListProps> = ({
    messages,
    selectedId,
    onSelect,
    onMarkAsRead,
}) => {
    const parentRef = useRef<HTMLDivElement>(null);

    const { flatItems } = useMemo(() => {
        const items: ListItem[] = [];
        const msgIndexMap = new Map<number, number>();
        
        let currentDate = '';
        let messageCounter = 0;

        messages.forEach((message, idx) => {
            const dateGroup = formatMessageDateGroup(message.timestamp);
            
            if (dateGroup !== currentDate) {
                items.push({
                    type: 'header',
                    date: dateGroup,
                    id: `header-${dateGroup}`,
                });
                currentDate = dateGroup;
            }

            msgIndexMap.set(idx, items.length);
            items.push({
                type: 'message',
                message,
                index: idx,
            });
            messageCounter++;
        });

        return { flatItems: items, messageIndexMap: msgIndexMap };
    }, [messages]);

    const virtualizer = useVirtualizer({
        count: flatItems.length,
        getScrollElement: () => parentRef.current,
        estimateSize: (index) => {
            const item = flatItems[index];
            return item.type === 'header' ? 32 : MESSAGES_PAGE_CONFIG.virtualItemHeight;
        },
        overscan: MESSAGES_PAGE_CONFIG.overscan,
    });

    if (messages.length === 0) {
        return <EmptyState />;
    }

    return (
        <div ref={parentRef} className="flex-1 overflow-auto px-[18px] pb-4">
            <div
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                <AnimatePresence mode="popLayout">
                    {virtualizer.getVirtualItems().map((virtualItem: VirtualItem) => {
                        const item = flatItems[virtualItem.index];

                        if (item.type === 'header') {
                            return (
                                <motion.div
                                    key={item.id}
                                    data-index={virtualItem.index}
                                    ref={virtualizer.measureElement}
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        transform: `translateY(${virtualItem.start}px)`,
                                    }}
                                    className="py-1"
                                >
                                    <div className="sticky top-0 z-10 py-2 px-3 bg-[var(--bg)]/80 backdrop-blur-sm">
                                        <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {item.date}
                                        </span>
                                        <div className="mt-1 h-px bg-gradient-to-r from-gray-200/50 via-gray-200 to-gray-200/50 dark:from-white/10 dark:via-white/10 dark:to-white/10" />
                                    </div>
                                </motion.div>
                            );
                        }

                        const { message, index } = item;
                        
                        return (
                            <motion.div
                                key={message.id}
                                data-index={virtualItem.index}
                                ref={virtualizer.measureElement}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${virtualItem.start}px)`,
                                }}
                                className="py-1"
                            >
                                <MessageCard
                                    message={message}
                                    isSelected={selectedId === message.id}
                                    onClick={() => onSelect(message)}
                                    onMarkAsRead={() => onMarkAsRead(message.id)}
                                    index={index}
                                />
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </div>
    );
};